import type {
  CustomerManagerUsageDailyByDayModelRow,
  CustomerManagerUsageDailyDay,
  CustomerManagerUsageDailyTotals,
} from '@nadoutong/contracts';
import { weekdayOfBusinessDate } from './usageWindows.ts';

export type UsageTrendMetric = 'usd' | 'reqs' | 'tok';
export type UsageTrendMode = 'point' | 'cum';
export type UsageShareMetric = 'spend' | 'requests' | 'tokens';

export const EMPTY_DAILY_TOTALS: CustomerManagerUsageDailyTotals = {
  request_count: 0,
  success_count: 0,
  error_count: 0,
  rate_limited_count: 0,
  input_tokens: 0,
  output_tokens: 0,
  cache_read_tokens: 0,
  cache_write_tokens: 0,
  cache_eligible_input_tokens: 0,
  usd_charged_micros: 0,
  usd_charged_cents: 0,
};

export function metricValue(row: CustomerManagerUsageDailyTotals, metric: UsageTrendMetric): number {
  if (metric === 'usd') return row.usd_charged_micros;
  if (metric === 'reqs') return row.request_count;
  return row.input_tokens + row.output_tokens;
}

export function cumulative(values: readonly number[]): readonly number[] {
  let sum = 0;
  return values.map((value) => {
    sum += value;
    return sum;
  });
}

export function alignPrevious(
  current: readonly CustomerManagerUsageDailyDay[],
  previous: readonly CustomerManagerUsageDailyDay[],
): readonly CustomerManagerUsageDailyDay[] {
  return current.map((_, index) => previous[index] ?? { date: '', ...EMPTY_DAILY_TOTALS });
}

export function seriesValues(
  days: readonly CustomerManagerUsageDailyDay[],
  metric: UsageTrendMetric,
  mode: UsageTrendMode,
): readonly number[] {
  const point = days.map((day) => metricValue(day, metric));
  return mode === 'cum' ? cumulative(point) : point;
}

/**
 * Sum through `lastIndex` (inclusive), interpolating the last bucket by
 * `fraction` when the caller has a complete previous bucket to scale.
 * `lastIndex < 0` is zero.
 */
export function sumThrough(
  values: readonly number[],
  lastIndex: number,
  fraction: number,
): number {
  if (lastIndex < 0) return 0;
  let sum = 0;
  const fullThrough = Math.min(lastIndex, values.length - 1);
  for (let i = 0; i < fullThrough; i += 1) {
    sum += values[i] ?? 0;
  }
  if (fullThrough >= 0 && fullThrough < values.length) {
    sum += (values[fullThrough] ?? 0) * fraction;
  }
  return sum;
}

export function deltaPercent(current: number, previous: number): number | null {
  if (!(previous > 0) || !Number.isFinite(current) || !Number.isFinite(previous)) return null;
  const pct = (current / previous - 1) * 100;
  return Number.isFinite(pct) ? pct : null;
}

export function formatDelta(pct: number): string {
  const abs = Math.abs(pct).toFixed(1);
  return pct >= 0 ? `+${abs}%` : `-${abs}%`;
}

const DAY_BUCKET = /^(\d{4}-\d{2}-\d{2})$/;
const HOUR_BUCKET = /^(\d{4}-\d{2}-\d{2}) (\d{2})$/;

export function bucketTimeLabel(
  date: string,
  weekdayLabel: (weekday: number) => string,
): string {
  const hour = HOUR_BUCKET.exec(date);
  if (hour !== null) {
    const ymd = hour[1] ?? date;
    const hh = hour[2] ?? '00';
    return `${ymd.slice(5)} ${weekdayLabel(weekdayOfBusinessDate(ymd))} ${hh}:00`;
  }
  if (!DAY_BUCKET.test(date)) return date;
  return `${date.slice(5)} ${weekdayLabel(weekdayOfBusinessDate(date))}`;
}

export function bucketAxisLabel(date: string): string {
  const hour = HOUR_BUCKET.exec(date);
  if (hour !== null) return `${hour[2] ?? '00'}:00`;
  return date;
}

export interface UsageShareRow {
  readonly key: string;
  readonly label: string;
  readonly secondary: string | null;
  readonly request_count: number;
  readonly input_tokens: number;
  readonly output_tokens: number;
  readonly usd_charged_micros: number;
  readonly usd_charged_cents: number;
}

function addShareRow(
  into: Map<string, UsageShareRow>,
  key: string,
  label: string,
  secondary: string | null,
  row: CustomerManagerUsageDailyTotals,
): void {
  const existing = into.get(key);
  if (existing === undefined) {
    into.set(key, {
      key,
      label,
      secondary,
      request_count: row.request_count,
      input_tokens: row.input_tokens,
      output_tokens: row.output_tokens,
      usd_charged_micros: row.usd_charged_micros,
      usd_charged_cents: row.usd_charged_cents,
    });
    return;
  }
  into.set(key, {
    ...existing,
    request_count: existing.request_count + row.request_count,
    input_tokens: existing.input_tokens + row.input_tokens,
    output_tokens: existing.output_tokens + row.output_tokens,
    usd_charged_micros: existing.usd_charged_micros + row.usd_charged_micros,
    usd_charged_cents: existing.usd_charged_cents + row.usd_charged_cents,
  });
}

function shareMetricValue(row: UsageShareRow, metric: UsageShareMetric): number {
  if (metric === 'spend') return row.usd_charged_micros;
  if (metric === 'requests') return row.request_count;
  return row.input_tokens + row.output_tokens;
}

export interface UsageShareBreakdown {
  readonly rows: readonly UsageShareRow[];
  readonly other: UsageShareRow | null;
  readonly otherCount: number;
  readonly total: number;
}

const SHARE_TOP_N = 7;

export function breakdownByModel(
  rows: readonly CustomerManagerUsageDailyByDayModelRow[],
  metric: UsageShareMetric,
): UsageShareBreakdown {
  return collapseShare(
    rows,
    (row) => {
      if (row.model === null) return null;
      return { key: `${row.vendor ?? ''}\u001f${row.model}`, label: row.model, secondary: row.vendor };
    },
    metric,
  );
}

export function breakdownBySku(
  rows: readonly CustomerManagerUsageDailyByDayModelRow[],
  metric: UsageShareMetric,
): UsageShareBreakdown {
  return collapseShare(
    rows,
    (row) => {
      if (row.sku_id === null && row.sku_name === null) return null;
      const label = row.sku_name ?? row.sku_id ?? '';
      return { key: row.sku_id ?? `name:${label}`, label, secondary: null };
    },
    metric,
  );
}

function collapseShare(
  rows: readonly CustomerManagerUsageDailyByDayModelRow[],
  identity: (
    row: CustomerManagerUsageDailyByDayModelRow,
  ) => { readonly key: string; readonly label: string; readonly secondary: string | null } | null,
  metric: UsageShareMetric,
): UsageShareBreakdown {
  const named = new Map<string, UsageShareRow>();
  const remainder: CustomerManagerUsageDailyTotals[] = [];
  let foldedGroups = 0;
  for (const row of rows) {
    const id = identity(row);
    if (id === null) {
      remainder.push(row);
      foldedGroups += 1;
      continue;
    }
    addShareRow(named, id.key, id.label, id.secondary, row);
  }
  const ranked = [...named.values()].sort((a, b) => shareMetricValue(b, metric) - shareMetricValue(a, metric));
  const top = ranked.slice(0, SHARE_TOP_N);
  const rest = ranked.slice(SHARE_TOP_N);
  const otherParts = [
    ...rest,
    ...remainder.map((row) => ({
      key: 'other',
      label: '',
      secondary: null,
      request_count: row.request_count,
      input_tokens: row.input_tokens,
      output_tokens: row.output_tokens,
      usd_charged_micros: row.usd_charged_micros,
      usd_charged_cents: row.usd_charged_cents,
    })),
  ];
  const other =
    otherParts.length === 0
      ? null
      : otherParts.reduce<UsageShareRow>(
          (sum, row) => ({
            key: 'other',
            label: '',
            secondary: null,
            request_count: sum.request_count + row.request_count,
            input_tokens: sum.input_tokens + row.input_tokens,
            output_tokens: sum.output_tokens + row.output_tokens,
            usd_charged_micros: sum.usd_charged_micros + row.usd_charged_micros,
            usd_charged_cents: sum.usd_charged_cents + row.usd_charged_cents,
          }),
          {
            key: 'other',
            label: '',
            secondary: null,
            request_count: 0,
            input_tokens: 0,
            output_tokens: 0,
            usd_charged_micros: 0,
            usd_charged_cents: 0,
          },
        );
  const otherEmpty =
    other === null ||
    (other.request_count === 0 && other.usd_charged_micros === 0 && other.input_tokens === 0 && other.output_tokens === 0);
  const visible = otherEmpty || other === null ? top : [...top, other];
  const total = visible.reduce((sum, row) => sum + shareMetricValue(row, metric), 0);
  return {
    rows: top,
    other: otherEmpty ? null : other,
    otherCount: rest.length + (foldedGroups > 0 ? 1 : 0),
    total,
  };
}

export function shareValue(row: UsageShareRow, metric: UsageShareMetric): number {
  return shareMetricValue(row, metric);
}

export function leadModelByDate(
  rows: readonly CustomerManagerUsageDailyByDayModelRow[],
): ReadonlyMap<string, { readonly model: string; readonly share: number }> {
  const byDate = new Map<string, CustomerManagerUsageDailyByDayModelRow[]>();
  for (const row of rows) {
    const list = byDate.get(row.date) ?? [];
    list.push(row);
    byDate.set(row.date, list);
  }
  const out = new Map<string, { readonly model: string; readonly share: number }>();
  for (const [date, list] of byDate) {
    const named = list.filter((row) => row.model !== null);
    const total = named.reduce((sum, row) => sum + row.usd_charged_micros, 0);
    const lead = [...named].sort((a, b) => b.usd_charged_micros - a.usd_charged_micros)[0];
    if (lead === undefined || lead.model === null) continue;
    out.set(date, {
      model: lead.model,
      share: total > 0 ? (lead.usd_charged_micros / total) * 100 : 0,
    });
  }
  return out;
}

export function isEmptyUsage(totals: CustomerManagerUsageDailyTotals): boolean {
  return (
    totals.request_count === 0 &&
    totals.usd_charged_micros === 0 &&
    totals.input_tokens === 0 &&
    totals.output_tokens === 0
  );
}

/**
 * Overall cache hit rate as a percentage of protocol-eligible prompt tokens.
 *
 * The denominator is `cache_eligible_input_tokens`, which the gateway
 * computes after branching on protocol. `input_tokens` is not a safe
 * denominator: Anthropic/Gemini exclude cache read/write from it, while
 * OpenAI includes `cache_read` in it. Re-deriving inbound as
 * `input + cache_read + cache_write` double-counts OpenAI cache reads.
 * The sole source of truth for this denominator is gateway
 * `apps/platform-api/src/gateway-usage/routes/usage-cache-eligibility.ts`.
 *
 * Returns null when the denominator is ≤ 0 or non-finite so the caller
 * can render a placeholder instead of `NaN`.
 */
export function cacheHitRate(totals: {
  readonly cache_read_tokens: number;
  readonly cache_eligible_input_tokens: number;
}): number | null {
  const eligible = totals.cache_eligible_input_tokens;
  if (!(eligible > 0) || !Number.isFinite(eligible) || !Number.isFinite(totals.cache_read_tokens)) {
    return null;
  }
  return (totals.cache_read_tokens / eligible) * 100;
}
